import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faStar, faMoneyBill, faChartLine } from "@fortawesome/free-solid-svg-icons";
import { FaPaperPlane } from "react-icons/fa6";
import { useCart } from "../context/CartContext.js";
import CounsellingModal from "../pages/CounsellingModal.js";
import { getCookie } from "utils/index";
import Image from "next/image";
import LoginPromptModal from "../components/homes/heros/LoginPromptModal"; // Import LoginPromptModal

const RecentColleges = ({ tag, onCardClick, gridCols = 4 }) => {
  const router = useRouter();
  const { addToCart } = useCart();
  const [recentColleges, setRecentColleges] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false); // State for login prompt modal
  const [selectedPost, setSelectedPost] = useState(null); // State for selected post
  const authToken = getCookie("authToken");

  const fetchRecentColleges = async (tag) => {
    const baseURL = process.env.NEXT_PUBLIC_GHOST_API_URL;
    const apiKey = process.env.NEXT_PUBLIC_GHOST_API_KEY;
    try {
      const res = await axios.get(
        `${baseURL}/ghost/api/v3/content/posts/?key=${apiKey}&filter=tag:${tag}&limit=8`
      );
      setRecentColleges(res.data.posts);
    } catch (error) {
      console.error("Error fetching recent colleges:", error);
    }
  };

  useEffect(() => {
    if (tag) {
      fetchRecentColleges(tag);
    }
  }, [tag]);

  const handleAddToCart = (college) => {
    const itemWithImage = { ...college, image: college.feature_image?.replace("ghost.", "") };
    addToCart(itemWithImage);
    if (authToken) {
      const cartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
      cartItems.push(itemWithImage);
      localStorage.setItem("cartItems", JSON.stringify(cartItems));
    }
  };

  const handleStartCounselling = (post) => {
    const title = encodeURIComponent(post.title);
    const customExcerpt = JSON.parse(post.custom_excerpt || "{}");
    const location = encodeURIComponent(customExcerpt.location || "Unknown Location");
    localStorage.setItem("counsellingTitle", title);
    localStorage.setItem("counsellingLocation", location);

    if (authToken) {
      window.location.href = `https://portal.neetsupport.com/dashboard-Counselling?title=${title}&location=${location}`;
    } else {
      setSelectedPost(post);
      setIsLoginPromptOpen(true);
    }
  };

  const handleLoginSuccess = () => {
    const title = localStorage.getItem("counsellingTitle");
    const location = localStorage.getItem("counsellingLocation");
    window.location.href = `https://portal.neetsupport.com/dashboard-Counselling?title=${title}&location=${location}`;
  };

  const handleStartApplication = (post) => {
    let customExcerpt = {};
    try {
      customExcerpt = JSON.parse(post.custom_excerpt || "{}");
    } catch (error) {
      console.error("Error parsing custom_excerpt:", error);
    }

    setSelectedCollege({
      ...post,
      course: customExcerpt.courseOffered || [], // Pass course details
      location: customExcerpt.location || "Location Not Available", // Pass location
    });
    setIsModalOpen(true); // Open the modal
  };

  const handleCardClick = (slug) => {
    // tag "pg-counselling" but the page route is still /counselling/:slug.
    const routeBase = tag === "pg-counselling" ? "counselling" : tag;
    router.push(`/${routeBase}/${slug}`, undefined, { shallow: true }); // Use shallow routing to prevent full reload
    if (onCardClick) {
      onCardClick(slug); // Trigger the callback if provided
    }
  };

  const handleViewDetails = (slug) => {
    router.push(`/${tag}/${slug}`); // Ensure proper navigation without shallow routing
  };

  return (
    <div className="mt-12 px-5 md:px-10 xl:px-24">
      <h2 className="text-xl font-bold mb-6">
        {tag === "college"
          ? "Recent Updated Colleges"
          : tag === "pg-counselling"
          ? "Recent PG Counselling"
          : "Recent UG Counseling"}
      </h2>
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-${gridCols}`}>
        {recentColleges.map((post) => {
         let customExcerpt = {};
         try {
           customExcerpt = JSON.parse(post.custom_excerpt);
         } catch (error) {
           customExcerpt = { text: post.custom_excerpt }; // Store it as plain text if it's not JSON
         }
         
          return (
            <div key={post.id} className="p-2">
              {tag === "college" ? (
                <div
                  className="border rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow duration-300 w-full max-w-[350px] mx-auto flex-col h-full cursor-pointer"
                  onClick={() => handleCardClick(post.slug)} // ✅ Use updated handler
                >
                  <img
                    src={post.feature_image?.replace("ghost.", "") || "/default-image.jpg"}
                    alt={post.title}
                    layout="responsive"
                    width={350}
                    height={40}
                    className="rounded-lg w-full h-40 object-fill"
                  />
                  <div className="mt-4 flex-grow">
                    <h3 className="text-sm md:text-md font-semibold line-clamp-2 overflow-hidden text-ellipsis whitespace-normal">
                      {post.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500">
                      Course: {customExcerpt.courseOffered?.join(", ") || "N/A"}
                    </p>
                    <div className="mt-3 flex justify-between items-start">
                      <ul className="text-xs sm:text-sm text-gray-500 space-y-2">
                        <li className="flex items-center">
                          <FontAwesomeIcon
                            icon={faStar}
                            className="mr-2 text-yellow-500 w-4 h-4"
                          />
                          {customExcerpt.reviewCount || "0"} Reviews
                        </li>
                        <li className="flex items-center">
                          <FontAwesomeIcon
                            icon={faMapMarkerAlt}
                            className="mr-2 text-[#005F59] w-4 h-4"
                          />
                          {customExcerpt.location || "Unknown"}
                        </li>
                      </ul>
                      <ul className="text-xs sm:text-sm text-gray-500 space-y-2">
                        <li className="flex items-center">
                          <FontAwesomeIcon
                            icon={faMoneyBill}
                            className="mr-2 text-green-500 w-4 h-4"
                          />
                          Fees: {customExcerpt.fees || "N/A"}
                        </li>
                        <li className="flex items-center">
                          <FontAwesomeIcon
                            icon={faChartLine}
                            className="mr-2 text-blue-500 w-4 h-4"
                          />
                          Cutoff: {customExcerpt.cutoff || "N/A"}
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-between items-center">
                   
                    <div className="">
                      <button
                        className="relative text-[#ff9245] px-3 py-1 !ring-1 ring-[#ff9245] rounded-full transition-colors hover:text-[#fff] hover:bg-[#ff9245] text-xs md:text-sm duration-300"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(post.slug); // ✅ Use updated handler
                        }}
                      >
                        View Details
                      </button>
                    </div>
                    <div className="animate-blink rounded-full"> 
                    <button
                      className="px-3 py-1 text-xs md:text-sm rounded-full bg-[#ff9245] text-white border border-[#ff9245] hover:bg-white hover:!text-[#ff9245] hover:border-[#ff9245] hover:ring-1 hover:ring-[#ff9245] transition-colors duration-300 flex items-center"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleStartApplication(post); // Open CounsellingModal for colleges
                      }}
                    >
                      <FaPaperPlane className="mr-2" />
                      <span>Start Application</span>
                    </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="border rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow duration-300 w-full max-w-[350px] mx-auto flex-col h-full cursor-pointer"
                  onClick={() => handleCardClick(post.slug)} // ✅ Use updated handler
                >
                  <img
                    src={post.feature_image?.replace("ghost.", "") || "/default-image.jpg"}
                    alt={post.title}
                    className="rounded-lg w-full h-40 object-fill"
                  />
                  <div className="mt-4 flex-grow">
                    <h3 className="text-sm md:text-md font-semibold line-clamp-2 overflow-hidden text-ellipsis whitespace-normal">
                      {post.title}
                    </h3>
                    <div className="text-xs sm:text-sm text-gray-500 flex justify-between items-center mt-1">
                      <p className="flex items-center">
                        <FontAwesomeIcon
                          icon={faMapMarkerAlt}
                          className="mr-2 text-[#005F59] w-4 h-4"
                        />
                        {customExcerpt.location || "Unknown Location"}
                      </p>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-500 flex items-center mt-1">
                      <FontAwesomeIcon
                        icon={faStar}
                        className="mr-2 text-yellow-500 w-4 h-4"
                      />
                      {customExcerpt.reviewCount || 0} Reviews
                    </p>
                  </div>
                  <div className="mt-4 flex justify-between items-center">
                    
                    <div className="">
                      <button
                        className="relative text-[#ff9245] px-3 py-1 !ring-1 ring-[#ff9245] rounded-full transition-colors hover:text-[#fff] hover:bg-[#ff9245] text-xs md:text-sm duration-300"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(post.slug); // ✅ Use updated handler
                        }}
                      >
                        View Details
                      </button>
                    </div>
                    <div className="animate-blink rounded-full">
                    <button
                      className="px-3 py-1 text-xs md:text-sm rounded-full bg-[#ff9245] text-white border border-[#ff9245] hover:bg-white hover:!text-[#ff9245] hover:border-[#ff9245] hover:ring-1 hover:ring-[#ff9245] transition-colors duration-300 flex items-center"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleStartCounselling(post); // Call the handler
                      }}
                    >
                      <FaPaperPlane className="mr-2" />
                      <span>Start Counselling</span>
                    </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {selectedCollege && (
        <CounsellingModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          college={selectedCollege} // Pass college details
          course={selectedCollege.course} // Pass course details
          location={selectedCollege.location} // Pass location
        />
      )}
      {isLoginPromptOpen && (
        <LoginPromptModal
          onClose={() => setIsLoginPromptOpen(false)}
          onLoginSuccess={handleLoginSuccess}
          title={localStorage.getItem("counsellingTitle")}
          location={localStorage.getItem("counsellingLocation")}
          tag="Counselling"
        />
      )}
    </div>
  );
};

export default RecentColleges;
